/**
 * RMA processing form — inventory card: the immutable list of past
 * inventory adjustments plus the editable remove/add-to-inventory and
 * packing-supply quantity pickers driving this RMA's adjustments.
 *
 * Contains:
 * - InventorySection (default export)
 */

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SectionCard } from '../../components/ReturnSection';
import InventoryQtySelect from '../fields/InventoryQtySelect';

export default function InventorySection({
  pastInventoryAdjustments,
  isQuickShip,
  removeFromInventory,
  onRemoveFromInventoryChange,
  addToInventory,
  onAddToInventoryChange,
  packingSupplies,
  onPackingSuppliesChange,
}) {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('rma.inventory', 'Inventory')}>
      {!pastInventoryAdjustments.isEmpty() && (
        <Box sx={{ mb: 2, fontSize: '14px' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {t('rma.previousInventoryAdjustments', 'Previous Inventory Adjustments')}
            </Typography>
            <Tooltip
              title={t(
                'rma.inventoryAdjustmentsTooltip',
                'These inventory adjustments have already been made and cannot be reverted.',
              )}
              placement="top"
            >
              <Box component="span" sx={{ color: 'text.secondary', fontSize: '14px', cursor: 'default' }}>
                ⓘ
              </Box>
            </Tooltip>
          </Stack>
          <Box component="ul" sx={{ pl: 2, mt: 0 }}>
            {pastInventoryAdjustments.map((item, index) => (
              <li key={index}>
                {item.getIn(['product', 'item_part_num'], t('rma.noPartNumber', 'no part number'))} -{' '}
                {item.getIn(['product', 'item_name'], t('rma.noProductName', 'no product name'))} -{' '}
                {item.get('quantity_shipped', 0) > 0
                  ? t('rma.removedQty', 'Removed: {{qty}}', { qty: item.get('quantity_shipped', 0) })
                  : t('rma.addedQty', 'Added {{qty}}', { qty: item.get('quantity_added_to_inventory', 0) })}
              </li>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        {!isQuickShip ? (
          <InventoryQtySelect
            title={t('rma.removeItemsFromInventory', 'Remove Items from Inventory')}
            fetchParams={{ used_in_rma: true }}
            productFilter={p => p.used_in_rma === true}
            value={removeFromInventory}
            onQtyChange={onRemoveFromInventoryChange}
            defaultOpen={true}
          />
        ) : (
          <InventoryQtySelect
            title={t('rma.addItemsToInventory', 'Add Items to Inventory')}
            fetchParams={{ used_in_rma: true }}
            productFilter={p => p.used_in_rma === true}
            value={addToInventory}
            onQtyChange={onAddToInventoryChange}
            defaultOpen={true}
          />
        )}
      </Box>

      {!isQuickShip && (
        <Box sx={{ mb: 2 }}>
          <InventoryQtySelect
            title={t('rma.packingSupplies', 'Packing Supplies')}
            fetchParams={{ used_in_packing_supplies: true }}
            productFilter={p => p.used_in_packing_supplies === true}
            value={packingSupplies}
            onQtyChange={onPackingSuppliesChange}
            qtyChoices={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
          />
        </Box>
      )}
    </SectionCard>
  );
}
